import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "react-hot-toast";
import { ArrowLeftIcon, ArrowPathIcon, ArrowTopRightOnSquareIcon, ChevronDownIcon, PlusIcon, TrashIcon } from "@heroicons/react/24/outline";
import cn from "classnames";
import Button from "../../common/Button";
import Dropdown from "../../Settings/Dropdown";
import Reveal from "../../common/Reveal";
import { AgencyTagRow, DOC_TYPE_OPTIONS, DocumentDetailData, STAGE_LABELS, SectionRow, documentsApi, formatBytes } from "./api";
import { TagChip } from "./tagColors";

const POLL_MS = 5000;

interface Props {
  documentId: string;
  tags: AgencyTagRow[];
  onTagsChanged: () => Promise<void> | void;
}

// One document: its status, tag breakdown, and every section with editable
// tags. Admin tag edits survive a reprocess (re-attached by section key).
const DocumentDetail: React.FC<Props> = ({ documentId, tags, onTagsChanged }) => {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const focusSection = params.get("section");
  const [data, setData] = useState<DocumentDetailData | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [expanded, setExpanded] = useState<Record<string, string>>({});
  const [filter, setFilter] = useState("");
  const [tagFilter, setTagFilter] = useState("");
  const [picking, setPicking] = useState<string | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [busy, setBusy] = useState(false);
  const scrolled = useRef(false);

  const load = useCallback(async () => {
    try {
      setData(await documentsApi.get(documentId));
      setError(null);
    } catch (err: any) {
      setError(err?.response?.status === 404 ? "This document no longer exists." : "Could not load the document.");
    }
  }, [documentId]);

  useEffect(() => {
    load();
  }, [load]);

  const processing = data && data.document.status !== "completed" && data.document.status !== "failed";
  useEffect(() => {
    if (!processing) return;
    const t = setInterval(load, POLL_MS);
    return () => clearInterval(t);
  }, [processing, load]);

  useEffect(() => {
    if (!data || !focusSection || scrolled.current) return;
    const el = document.getElementById(`section-${focusSection}`);
    if (el) {
      scrolled.current = true;
      el.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  }, [data, focusSection]);

  const tagById = useMemo(() => new Map(tags.map((t) => [t.id, t])), [tags]);

  const sections = useMemo(() => {
    if (!data) return [];
    const q = filter.trim().toLowerCase();
    return data.sections.filter(
      (s) =>
        (!q || (s.heading || "").toLowerCase().includes(q) || (s.section_number || "").toLowerCase().includes(q) || s.excerpt.toLowerCase().includes(q)) &&
        (!tagFilter || s.tags.some((t) => t.tagId === tagFilter))
    );
  }, [data, filter, tagFilter]);

  const patchSection = (sectionId: string, next: SectionRow["tags"]) =>
    setData((d) => (d ? { ...d, sections: d.sections.map((s) => (s.id === sectionId ? { ...s, tags: next } : s)) } : d));

  const changeTags = async (section: SectionRow, body: { add?: string[]; remove?: string[]; create?: { name: string }[] }) => {
    try {
      const res = await documentsApi.setSectionTags(documentId, section.id, body);
      patchSection(section.id, res.tags);
      if (body.create?.length) await onTagsChanged();
      // The document's tag breakdown changed too.
      load();
    } catch (err: any) {
      toast.error(err?.response?.data?.error || "Could not update tags");
    }
  };

  const expand = async (s: SectionRow) => {
    if (expanded[s.id]) {
      setExpanded((e) => {
        const next = { ...e };
        delete next[s.id];
        return next;
      });
      return;
    }
    const full = await documentsApi.section(documentId, s.id);
    setExpanded((e) => ({ ...e, [s.id]: full.content }));
  };

  const reprocess = async () => {
    try {
      await documentsApi.reprocess(documentId);
      toast.success("Reprocessing started. Your tag edits will be kept.");
      load();
    } catch (err: any) {
      toast.error(err?.response?.data?.error || "Could not reprocess");
    }
  };

  const remove = async () => {
    setBusy(true);
    try {
      await documentsApi.remove(documentId);
      toast.success("Document deleted");
      navigate("/agency/dashboard/documents");
    } catch (err: any) {
      toast.error(err?.response?.data?.error || "Could not delete");
      setBusy(false);
    }
  };

  const setType = async (docType: string) => {
    if (!data) return;
    const updated = await documentsApi.update(documentId, { docType: docType as any });
    setData({ ...data, document: { ...data.document, doc_type: updated.doc_type } });
  };

  if (error) {
    return (
      <div className="py-12 text-center">
        <p className="text-base-content/70">{error}</p>
        <Button variant="secondary" size="sm" className="mt-4" onClick={() => navigate("/agency/dashboard/documents")}>
          <ArrowLeftIcon className="h-4 w-4 mr-1" /> Documents
        </Button>
      </div>
    );
  }
  if (!data) return <div className="py-12 text-center text-sm text-base-content/60">Loading…</div>;
  const { document: doc } = data;

  return (
    <div>
      <div className="flex items-start gap-3 flex-wrap">
        <Button variant="secondary" size="sm" className="mt-0.5" onClick={() => navigate("/agency/dashboard/documents")} title="All documents">
          <ArrowLeftIcon className="h-4 w-4 mr-1" /> Documents
        </Button>
        <div className="min-w-0 flex-1">
          <h1 className="text-xl font-bold text-base-content truncate">{doc.title}</h1>
          <p className="text-xs text-base-content/60">
            {doc.file_name}
            {doc.file_size ? ` · ${formatBytes(doc.file_size)}` : ""}
            {doc.page_count ? ` · ${doc.page_count} page${doc.page_count === 1 ? "" : "s"}` : ""}
            {doc.section_count ? ` · ${doc.section_count} section${doc.section_count === 1 ? "" : "s"}` : ""}
            {" · "}
            {doc.status === "completed" ? (
              <span className="text-green-700 dark:text-green-400">Ready</span>
            ) : doc.status === "failed" ? (
              <span className="text-red-600 dark:text-red-400">Failed{doc.error_message ? `: ${doc.error_message}` : ""}</span>
            ) : (
              <span className="text-amber-700 dark:text-amber-400">
                <span className="inline-block size-2 rounded-full bg-amber-500 animate-pulse mr-1" />
                {STAGE_LABELS[doc.stage] || doc.stage}
                {(doc.attempts || 0) > 1 ? ` (attempt ${doc.attempts})` : ""}
              </span>
            )}
          </p>
        </div>
        <div className="flex items-center gap-1">
          <Dropdown size="sm" dense options={DOC_TYPE_OPTIONS} value={doc.doc_type} onChange={setType} />
          <Button variant="secondary" size="sm" onClick={async () => window.open(await documentsApi.viewUrl(documentId), "_blank", "noopener")} title="Open the file">
            <ArrowTopRightOnSquareIcon className="h-4 w-4 mr-1" /> View
          </Button>
          <Button variant="secondary" size="sm" className="px-2" title="Reprocess (tag edits are kept)" disabled={doc.status === "processing"} onClick={reprocess}>
            <ArrowPathIcon className={cn("size-4", processing && "animate-spin")} />
          </Button>
          <Button variant="secondary" size="sm" className="px-2 hover:text-red-600 dark:hover:text-red-400" title="Delete document" onClick={() => setDeleting(true)}>
            <TrashIcon className="size-4" />
          </Button>
        </div>
      </div>

      {doc.droppedOverrides > 0 && (
        <div className="mt-3 text-xs rounded-md bg-amber-50 dark:bg-amber-950/40 text-amber-800 dark:text-amber-400 px-3 py-1.5">
          {doc.droppedOverrides} tag edit{doc.droppedOverrides === 1 ? "" : "s"} could not be re-applied after reprocessing because the section split changed.
        </div>
      )}

      {doc.tags.length > 0 && (
        <div className="mt-4 flex flex-wrap items-center gap-1.5">
          <span className="text-xs text-base-content/50 mr-1">Topics</span>
          {doc.tags.map((t) => (
            <TagChip key={t.id} name={t.name} color={t.color} count={t.sections} onClick={() => setTagFilter(tagFilter === t.id ? "" : t.id)} className={cn(tagFilter && tagFilter !== t.id && "opacity-50")} />
          ))}
          {tagFilter && (
            <button className="text-xs text-blue-600 dark:text-blue-300 hover:underline ml-1" onClick={() => setTagFilter("")}>
              clear
            </button>
          )}
        </div>
      )}

      <div className="mt-6 flex items-center gap-3">
        <h2 className="text-base font-semibold text-base-content">Sections</h2>
        <input className="input input-bordered input-sm w-64 bg-base-100 focus:outline-none focus:border-blue-500" placeholder="Filter sections" value={filter} onChange={(e) => setFilter(e.target.value)} />
        <span className="text-xs text-base-content/50">Dashed tags were assigned automatically; solid ones were set by an admin. Click × to remove a tag; it will not come back on reprocess.</span>
      </div>

      {data.sections.length === 0 ? (
        <p className="mt-4 text-sm text-base-content/60">{processing ? "Sections appear once the document has been read." : "No sections were found in this document."}</p>
      ) : (
        <ul className="mt-3 space-y-2">
          {sections.map((s, i) => {
            const open = Boolean(expanded[s.id]);
            const focused = focusSection === s.id;
            return (
              <Reveal as="li" index={i} key={s.id}>
                <div id={`section-${s.id}`} className={cn("rounded-lg border bg-base-100 p-3 transition-colors", focused ? "border-blue-400 ring-1 ring-blue-300" : "border-base-300")}>
                  <div className="flex items-start gap-3">
                    <div className="min-w-0 flex-1">
                      <button className="text-left group" onClick={() => expand(s)}>
                        <p className="text-sm font-medium text-base-content">
                          {s.section_number && <span className="text-blue-600 dark:text-blue-300 mr-1.5">{s.section_number}</span>}
                          {s.heading || <span className="text-base-content/50 italic">Untitled section</span>}
                          <ChevronDownIcon className={cn("inline size-3.5 ml-1 text-base-content/40 transition-transform", open && "rotate-180")} />
                        </p>
                      </button>
                      <p className="text-[11px] text-base-content/50">
                        {s.page_start != null ? `p.${s.page_start}${s.page_end != null && s.page_end !== s.page_start ? `-${s.page_end}` : ""} · ` : ""}
                        {s.char_count.toLocaleString()} characters
                        {s.section_path && s.section_path !== s.section_number ? ` · ${s.section_path}` : ""}
                      </p>
                      <p className={cn("mt-1 text-xs text-base-content/70 whitespace-pre-line", !open && "line-clamp-2")}>{open ? expanded[s.id] : s.excerpt}</p>
                    </div>
                    <div className="shrink-0 max-w-[45%] flex flex-wrap justify-end gap-1 relative">
                      {s.tags.map((l) => {
                        const t = tagById.get(l.tagId);
                        if (!t) return null;
                        return (
                          <TagChip
                            key={l.tagId}
                            name={t.name}
                            color={t.color}
                            auto={l.source === "auto"}
                            title={l.source === "auto" ? `Assigned automatically${l.confidence != null ? ` (${Math.round(l.confidence * 100)}%)` : ""}` : "Set by an admin"}
                            onRemove={() => changeTags(s, { remove: [l.tagId] })}
                          />
                        );
                      })}
                      <button
                        className="inline-flex items-center gap-0.5 rounded-full border border-dashed border-base-content/30 px-2 py-0.5 text-[11px] text-base-content/60 hover:border-blue-400 hover:text-blue-600 dark:hover:text-blue-300"
                        onClick={() => setPicking(picking === s.id ? null : s.id)}
                        title="Add a tag"
                      >
                        <PlusIcon className="size-3" /> tag
                      </button>
                      {picking === s.id && (
                        <TagChipPicker
                          tags={tags.filter((t) => !s.tags.some((l) => l.tagId === t.id))}
                          onPick={(id) => {
                            setPicking(null);
                            changeTags(s, { add: [id] });
                          }}
                          onCreate={(name) => {
                            setPicking(null);
                            changeTags(s, { create: [{ name }] });
                          }}
                          onClose={() => setPicking(null)}
                        />
                      )}
                    </div>
                  </div>
                </div>
              </Reveal>
            );
          })}
          {sections.length === 0 && <li className="text-sm text-base-content/60 py-4">No sections match.</li>}
        </ul>
      )}

      {deleting && (
        <div className="modal modal-open" role="dialog">
          <div className="modal-box max-w-md">
            <h3 className="font-bold text-lg">Delete “{doc.title}”?</h3>
            <p className="text-sm text-base-content/70 mt-2">Its sections, tags, and index are removed and new reports stop citing it. Reports already written keep the sections they cited.</p>
            <div className="modal-action">
              <Button variant="outline" onClick={() => setDeleting(false)} disabled={busy}>
                Cancel
              </Button>
              <Button variant="danger" onClick={remove} loading={busy}>
                Delete
              </Button>
            </div>
          </div>
          <div className="modal-backdrop" onClick={() => !busy && setDeleting(false)} />
        </div>
      )}
    </div>
  );
};

// Search the vocabulary or create a tag on the spot.
const TagChipPicker: React.FC<{ tags: AgencyTagRow[]; onPick: (id: string) => void; onCreate: (name: string) => void; onClose: () => void }> = ({ tags, onPick, onCreate, onClose }) => {
  const [q, setQ] = useState("");
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const onDown = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) onClose();
    };
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && onClose();
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [onClose]);
  const query = q.trim().toLowerCase();
  const matches = tags.filter((t) => !query || t.name.toLowerCase().includes(query)).slice(0, 10);
  const exact = tags.some((t) => t.name.toLowerCase() === query);
  return (
    <div ref={ref} className="absolute right-0 top-full mt-1 z-20 w-64 rounded-xl border border-base-300 bg-base-100 shadow-lg p-2">
      <input
        autoFocus
        className="input input-bordered input-sm w-full bg-base-100 focus:outline-none focus:border-blue-500"
        placeholder="Search or create a tag"
        value={q}
        onChange={(e) => setQ(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Enter") {
            e.preventDefault();
            if (matches.length === 1 || exact) onPick((tags.find((t) => t.name.toLowerCase() === query) || matches[0]).id);
            else if (query.length >= 2) onCreate(q.trim());
          }
        }}
      />
      <ul className="mt-2 max-h-48 overflow-auto space-y-0.5">
        {matches.map((t) => (
          <li key={t.id}>
            <button className="w-full text-left px-2 py-1 rounded-md hover:bg-base-200 flex items-center gap-2" onClick={() => onPick(t.id)}>
              <TagChip name={t.name} color={t.color} />
            </button>
          </li>
        ))}
        {query.length >= 2 && !exact && (
          <li>
            <button className="w-full text-left px-2 py-1 rounded-md hover:bg-base-200 text-sm text-blue-600 dark:text-blue-300" onClick={() => onCreate(q.trim())}>
              Create “{q.trim()}”
            </button>
          </li>
        )}
        {!matches.length && query.length < 2 && <li className="px-2 py-1 text-xs text-base-content/50">Type to search tags</li>}
      </ul>
    </div>
  );
};

export default DocumentDetail;
